import { useMemo, useState } from 'react';
import {
  PPCO_SECTIONS,
  emptyPpcoAnswers,
  ppcoAnswersComplete,
  synthesizePpcoConclusion,
  type PpcoAnswers,
  type PpcoEvaluation,
} from '@isbd/shared';
import { invokeAiFunction, kuunda } from '@/lib/kuunda';
import { useToast } from '@/features/ui/toast/useToast';

interface Props {
  ideationId: string;
  businessIdea: string;
  onCompleted?: (evaluation: PpcoEvaluation) => void;
}

export function PpcoPanel({ ideationId, businessIdea, onCompleted }: Props) {
  const toast = useToast();
  const [answers, setAnswers] = useState<PpcoAnswers>(emptyPpcoAnswers);
  const [evaluation, setEvaluation] = useState<PpcoEvaluation | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const complete = useMemo(() => ppcoAnswersComplete(answers), [answers]);

  async function runValidation() {
    if (!complete) {
      toast.error('Répondez à chaque question (min. 12 caractères).');
      return;
    }
    setLoading(true);
    try {
      const result = await invokeAiFunction<PpcoEvaluation>('ai-analyze', {
        kind: 'ppco',
        ideation_id: ideationId,
        business_idea: businessIdea,
        answers,
      });
      const score = Number(result.score_final) || 0;
      const normalized: PpcoEvaluation = {
        sections: result.sections ?? [],
        score_final: score,
        conclusion: result.conclusion || synthesizePpcoConclusion(score),
        recommendation: result.recommendation || '',
      };
      setEvaluation(normalized);
      const { error: upErr } = await kuunda
        .from('ideations')
        .update({
          ppco_evaluation: { answers, evaluation: normalized },
          ppco_score: normalized.score_final,
          ppco_conclusion: normalized.conclusion,
          status: 'ppco_done',
        })
        .eq('id', ideationId);
      if (upErr) throw upErr;
      toast.success('Évaluation PPCO enregistrée.', 'PPCO');
      onCompleted?.(normalized);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Validation PPCO impossible');
    } finally {
      setLoading(false);
    }
  }

  const section = PPCO_SECTIONS[activeSection]!;

  return (
    <div className="panel" style={{ marginTop: '1.25rem' }}>
      <h3 style={{ marginTop: 0 }}>Validation PPCO détaillée</h3>
      <p className="muted">
        Plus · Potentiel · Craintes · Opportunités — l’IA note vos réponses pour éclairer la décision.
      </p>

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {PPCO_SECTIONS.map((s, idx) => (
          <button
            key={s.key}
            type="button"
            className={`btn ${idx === activeSection ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActiveSection(idx)}
          >
            {s.title}
          </button>
        ))}
      </div>

      <div className="panel" style={{ boxShadow: 'none', background: 'rgba(74, 126, 176, 0.08)', borderColor: 'rgba(26, 60, 94, 0.16)' }}>
        <h4 style={{ marginTop: 0 }}>{section.title}</h4>
        <p className="muted">{section.description}</p>
        {section.questions.map((q) => (
          <div className="field" key={q.id}>
            <label htmlFor={q.id}>{q.question}</label>
            <textarea
              id={q.id}
              value={answers[q.id] ?? ''}
              onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
              placeholder="Votre réponse détaillée…"
            />
          </div>
        ))}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={activeSection === 0}
            onClick={() => setActiveSection((v) => Math.max(0, v - 1))}
          >
            Précédent
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={activeSection >= PPCO_SECTIONS.length - 1}
            onClick={() => setActiveSection((v) => Math.min(PPCO_SECTIONS.length - 1, v + 1))}
          >
            Suivant
          </button>
        </div>
      </div>

      <button
        type="button"
        className="btn btn-accent"
        style={{ marginTop: '0.75rem' }}
        disabled={loading || !complete}
        onClick={() => void runValidation()}
      >
        {loading ? 'Évaluation Grok…' : 'Valider le PPCO'}
      </button>

      {evaluation ? (
        <div style={{ marginTop: '1rem' }}>
          <p>
            Score final : <strong>{evaluation.score_final}</strong> — {evaluation.conclusion}
          </p>
          <ul>
            {evaluation.sections.map((s) => (
              <li key={s.key}>
                <strong>{s.title}</strong> : {s.score} — {s.comment}
              </li>
            ))}
          </ul>
          {evaluation.recommendation ? <p className="muted">{evaluation.recommendation}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
